import {
	queryOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query';

import { api } from '#client';
import { handleError, parseSchema } from '#client/validation';

import { fillData } from '../shared/data';
import {
	Categories,
	Data,
	DataBody,
	DataErrorKeys,
	FullData,
} from '../shared/data.types';
import { claimQueries } from './claims';

const getClaimData = (claimId: string): Promise<FullData> =>
	api
		.get(`/file/documentdata?claimId=${claimId}`)
		.then(parseSchema(Data))
		.then(fillData)
		.catch(handleError(DataErrorKeys));

const getExpenseCategories = () =>
	api.get('/claims/expense-categories').then(parseSchema(Categories));

const getRevenueCategories = () =>
	api.get('/claims/revenue-categories').then(parseSchema(Categories));

export const claimDataQueries = {
	all: ['claimData'],
	id: (claimId: string) =>
		queryOptions({
			queryKey: [...claimQueries.ids(), claimId, 'data'],
			queryFn: () => getClaimData(claimId),
		}),
	expenseCategories: () =>
		queryOptions({
			queryKey: [...claimDataQueries.all, 'expenseCategories'],
			queryFn: () => getExpenseCategories(),
		}),
	revenueCategories: () =>
		queryOptions({
			queryKey: [...claimDataQueries.all, 'revenueCategories'],
			queryFn: () => getRevenueCategories(),
		}),
};

const putClaimData = (claimId: string, data: DataBody) =>
	api.put(`/file/documentdata?claimId=${claimId}`, { json: data });

export const useMutateClaimData = (claimId: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: DataBody) => putClaimData(claimId, data),
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({
					queryKey: [...claimQueries.ids(), claimId],
				}),
			]);
		},
	});
};
